import { useTranslation } from 'react-i18next';
import { Icon, Typography, Button } from '@/components';
import { useDevelopmentStatus } from '@/hooks';
import { isItemNull } from '@/features/projects/helpers';
import { customRound, displayQuarter, europeanFormatNumber } from '@/helpers';
import { HidropowerItem } from '@/features/projects/types';

export type HidropowerBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function HidropowerBox({ normalizedProject, isEditable, onEdit }: HidropowerBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const hidropowerTech = normalizedProject.technologies.find((tech: any) => tech.type === 'hidropower');
  if (!hidropowerTech) return null;

  const item: HidropowerItem = hidropowerTech.details.item;
  const status = hidropowerTech.details.developmentStatus.status;
  if (!isEditable && !status && isItemNull(item)) return null;

  const roundedInstalledCapacity = item.installedCapacity && customRound(item.installedCapacity);
  const roundedTurbineCapacity = item.turbineCapacity && customRound(item.turbineCapacity);
  const roundedPumpCapacity = item.pumpCapacity && customRound(item.pumpCapacity);

  const fields = [
    {
      label: t('Installed Capacity'),
      value: item.installedCapacity ? `${europeanFormatNumber(roundedInstalledCapacity)} MW` : null,
    },
    { label: t('Type of Project'), value: item.typeProject ?? null },
    {
      label: t('Storage Hours'),
      value: item.storageHours ? `${europeanFormatNumber(item.storageHours)} h` : null,
    },
    {
      label: t('Specific Production'),
      value: item.specificProduction ? `${europeanFormatNumber(item.specificProduction)} h` : null,
    },
    { label: t('Development Status'), value: mapDevelopmentStatus(status) || null },
    { label: t('Ready to Build (RtB) Date'), value: item.readyToBuild ? displayQuarter(item.readyToBuild) : null },
    {
      label: t('Commercial Operation Date (COD)'),
      value: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    },
    {
      label: t('Turbine Capacity'),
      value: item.turbineCapacity ? `${europeanFormatNumber(roundedTurbineCapacity)} MW` : null,
    },
    {
      label: t('Pump Capacity'),
      value: item.pumpCapacity ? `${europeanFormatNumber(roundedPumpCapacity)} MW` : null,
    },
    {
      label: t('Generation/Storaged Capacity'),
      value: item.generationStorageCapacity ? `${europeanFormatNumber(item.generationStorageCapacity)} GWh` : null,
    },
  ];

  const visibleFields = isEditable
    ? fields
    : fields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="hidropower" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Hydropower Details & Design')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>

      {(isEditable || item.generalComments) && (
        <Typography as="p" size="body-2" weight="semibold" relaxed>
          {t('Other Comments')}:{' '}
          <span className="ml-3 text-base font-normal whitespace-pre-wrap text-neutral-900">
            {item.generalComments ?? (isEditable ? '—' : null)}
          </span>
        </Typography>
      )}
    </div>
  );
}
